/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { ScreenType } from 'client-code/previews';
import { ToDoItem, ToDoOfferItem, ToDoOfferItemToDoStatusEnum } from 'client-code/recordings';

import { ContentType } from '../../../core/enums/ContentType';
import { ServicesClientImpl } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import { Log } from '../../../core/logging';
import { ScreenTaskControllerTypes } from '../../../core/navigation';
import { NetworkManager } from '../../../core/network/NetworkManager';
import type { TivoPayloadAction } from '../../../core/tivo/tivo-redux-wrapper';
import { TOfferPreview, TOfferPreviewCustomData } from '../../../core/types';
import { UIOrchestrator, UIOrchestratorState } from '../../../core/uiorchestrator';
import {
    getTodoItemsFromPage,
    getTodoOfferItemTitle,
    mapToDoItemConflictToMessageKey,
} from '../../../views/SettingsScreen/Manage/ToDoList/ToDoList.utils';
import { WhisperTypes } from '../../../views/Whisper/WhisperTypes';
import { OverlayTypes } from '../../overlay/OverlayTypes';
import { showPCPinOverlayWithProgramRating } from '../../overlay/pcpinoverlay/pcpinoverlay.reducers';
import { hideOverlayDialog, showOverlayDialog } from '../../overlaydialog/overlaydialog.reducers';
import { IOverlayDialog } from '../../overlaydialog/overlaydialogprops';
import { programScreenTransition } from '../../screens/navigation/screensnavigation';
import { setScreenTaskController } from '../../screens/screens.reducers';
import { showWhisper } from '../../whisper/whisper.reducer';
import {
    ON_KEY_DOWN_TO_DO_LIST,
    ON_KEY_UP_TO_DO_LIST,
} from './navigation/todolist.navigation.types';
import {
    onSetToDoListSelectedRowPreviewAction,
    setCurrentPageToDoListAction,
    setGhostModeAction,
    setRetryFetchCounterAction,
    setToDoListApiErrorAction,
    setToDoListDataAction,
    setTotalCountToDoListAction,
    setVerticalPositionToDoListAction,
} from './todolist.actions';
import {
    ON_GO_NEXT_PAGE_TO_DO_LIST,
    ON_GO_PREVIOUS_PAGE_TO_DO_LIST,
    ON_RETRY_FETCH_TO_DO_LIST_WHEN_ERROR,
    ON_SET_CARET_VISIBILITY_TO_DO_LIST,
    ON_SET_CURRENT_PAGE_TO_DO_LIST,
    ON_SET_GHOST_MODE_TO_DO_LIST,
    ON_SET_TO_DO_LIST_API_ERROR,
    ON_SET_TO_DO_LIST_DATA,
    ON_SET_TO_DO_LIST_SELECTED_ROW_PREVIEW,
    ON_SET_TOTAL_COUNT_TO_DO_LIST,
    ON_SET_VERTICAL_POSITION_TO_DO_LIST,
} from './todolist.types';

const TAG = 'ToDoListReducers';

interface IResetOptions {
    resetError?: boolean;
    resetCounter?: boolean;
}

interface IToDoListState {
    verticalPosition: number;
    toDoItems: ToDoItem[];
    currentPage: number;
    totalCount: number;
    selectedTodoPreviewPromise: Promise<TOfferPreview> | null;
    ghostMode: boolean;
    isCaretVisible: boolean;
    isError: boolean;
    retryFetchCounter: number;
}

const initialState: IToDoListState = {
    verticalPosition: 0,
    toDoItems: [],
    currentPage: 0,
    totalCount: 0,
    selectedTodoPreviewPromise: null,
    ghostMode: false,
    isCaretVisible: false,
    isError: false,
    retryFetchCounter: 0,
};

const RECORD_INFO_MOUSE_TRAP = 'hideToDoInfoConflictOverlay';

// Singleton subscription manager for UIOrchestrator state changes
let uiOrchestratorSubscription: Function | null = null;

const subscribeToUIOrchestrator = (dispatch: Function) => {
    if (uiOrchestratorSubscription) return;

    const callback = (state: UIOrchestratorState) => {
        switch (state) {
            case UIOrchestratorState.ONE_PASS_CREATED:
            case UIOrchestratorState.ONE_PASS_DELETED:
            case UIOrchestratorState.ONE_PASS_MODIFIED:
            case UIOrchestratorState.RECORDING_CREATED:
            case UIOrchestratorState.RECORDING_DELETED:
            case UIOrchestratorState.RECORDING_MODIFIED:
            case UIOrchestratorState.RECORDING_RECOVERED:
            case UIOrchestratorState.RECORDING_PERSISTENCE_MODIFIED:
                dispatch(
                    resetToDoListState({
                        resetError: true,
                        resetCounter: true,
                    }),
                );
                break;
            default:
        }
    };

    UIOrchestrator.stateChangeListener.subscribe(callback);
    uiOrchestratorSubscription = callback;
};

export const unsubscribeFromUIOrchestratorToDoList = () => {
    if (uiOrchestratorSubscription) {
        UIOrchestrator.stateChangeListener.cancel(uiOrchestratorSubscription);
        uiOrchestratorSubscription = null;
    }
};

const todoListReducer = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<IToDoListState>>{},
) => {
    switch (type) {
        case ON_KEY_UP_TO_DO_LIST:
            return { ...state, verticalPosition: payload.verticalPosition };

        case ON_KEY_DOWN_TO_DO_LIST:
            return { ...state, verticalPosition: payload.verticalPosition };

        case ON_SET_VERTICAL_POSITION_TO_DO_LIST:
            return { ...state, verticalPosition: payload.verticalPosition };

        case ON_SET_GHOST_MODE_TO_DO_LIST:
            return { ...state, ghostMode: payload.ghostMode };

        case ON_SET_CARET_VISIBILITY_TO_DO_LIST:
            return { ...state, isCaretVisible: payload.isCaretVisible };

        case ON_SET_TOTAL_COUNT_TO_DO_LIST:
            return { ...state, totalCount: payload.totalCount };

        case ON_SET_TO_DO_LIST_DATA:
            return { ...state, toDoItems: payload.toDoItems };

        case ON_SET_CURRENT_PAGE_TO_DO_LIST:
            return { ...state, currentPage: payload.currentPage };

        case ON_GO_NEXT_PAGE_TO_DO_LIST:
            return { ...state, currentPage: state.currentPage + 1, verticalPosition: 0 };

        case ON_GO_PREVIOUS_PAGE_TO_DO_LIST:
            return {
                ...state,
                currentPage: state.currentPage - 1,
                verticalPosition: payload.verticalPosition,
            };
        case ON_SET_TO_DO_LIST_SELECTED_ROW_PREVIEW:
            return {
                ...state,
                selectedTodoPreviewPromise: payload.selectedTodoPreviewPromise,
            };
        case ON_SET_TO_DO_LIST_API_ERROR:
            return {
                ...state,
                isError: payload.isError,
            };
        case ON_RETRY_FETCH_TO_DO_LIST_WHEN_ERROR:
            return {
                ...state,
                retryFetchCounter: payload.retryFetchCounter,
            };

        default:
            return state;
    }
};

export const disableGhostModeToDoList = () => (dispatch: Function) => {
    dispatch(setGhostModeAction(false));
};

export const enableGhostModeToDoList = () => (dispatch: Function) => {
    dispatch(setGhostModeAction(true));
};

export const resetToDoListState =
    ({ resetError = false, resetCounter = false }: IResetOptions = {}) =>
    (dispatch: Function) => {
        dispatch(setVerticalPositionToDoListAction(0));
        dispatch(setGhostModeAction(false));
        dispatch(setToDoListDataAction([]));
        dispatch(setCurrentPageToDoListAction(0));
        dispatch(setTotalCountToDoListAction(0));
        dispatch(onSetToDoListSelectedRowPreviewAction(null));
        if (resetError) {
            dispatch(setToDoListApiErrorAction(false));
        }
        if (resetCounter) {
            dispatch(setRetryFetchCounterAction(0));
        }
    };

export const showRecordingConflictInfoOverlay =
    (currentItem: ToDoOfferItem) => (dispatch: Function, getState: Function) => {
        const { isActive: isAntiBurnActive } = getState().antiburnOsd;
        if (isAntiBurnActive) return;

        const { isOverlayDialogActive } = getState().overlayDialog;
        if (isOverlayDialogActive) dispatch(hideOverlayDialog());

        const services = new ServicesClientImpl();
        const shouldHideMetadata = services.shouldHideMetadata();
        const { title, description } = mapToDoItemConflictToMessageKey(currentItem);
        const overlayDialogProps: IOverlayDialog = {
            description,
            hasDimmer: true,
            hasSeperator: false,
            mouseTrapValue: RECORD_INFO_MOUSE_TRAP,
            options: [{ text: Locale.getString(LocaleKeys.OVERLAY_OPTION_OK_TEXT) }],
            overlayType: OverlayTypes.VOID,
            title,
            subtitle:
                shouldHideMetadata && currentItem.isAdult
                    ? Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN)
                    : getTodoOfferItemTitle(currentItem),
        };
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.RECORD_CONFLICT_INFO_OVERLAY));
        dispatch(showOverlayDialog(overlayDialogProps));
    };

export const onKeyOkNoTaskToDoList = () => async (dispatch: Function, getState: Function) => {
    const { toDoItems, currentPage, verticalPosition, selectedTodoPreviewPromise } =
        getState().todoList;
    const { actualItems } = getTodoItemsFromPage(toDoItems, currentPage);
    const selectedItem = actualItems?.[verticalPosition];

    if (!selectedItem) return;

    const currentOfferPreview: TOfferPreviewCustomData | null = await getCurrentOfferPreview(
        selectedItem,
        selectedTodoPreviewPromise,
    );

    if (!currentOfferPreview) {
        Log.e(TAG, 'Failed to fetch offer preview for the selected ToDo item.');
        return;
    }

    const isExplicitlyAdult = selectedItem.isAdult ?? false;
    const isContentPCViolated = new ServicesClientImpl().checkPCViolations({
        offerPreview: currentOfferPreview,
    });

    const goToProgramScreen = () => async () => {
        if (!selectedItem?.offerId) {
            Log.e(TAG, 'No offerId found for the selected ToDo item.');
            return;
        }
        subscribeToUIOrchestrator(dispatch);

        dispatch(
            programScreenTransition(
                {
                    id: selectedItem.offerId,
                    contentType: ContentType.OFFER_ID,
                },
                showWhisper,
            ),
        );
    };

    if (isExplicitlyAdult && isContentPCViolated) {
        dispatch(showPCPinOverlayWithProgramRating(goToProgramScreen, isExplicitlyAdult));
    } else {
        dispatch(goToProgramScreen());
    }
};

export const onLongPressOkToDoList = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const { isError, toDoItems, currentPage, verticalPosition } = getState().todoList;

    if (screenTaskController !== ScreenTaskControllerTypes.NO_TASK) {
        return;
    }

    if (handleToDoListErrorOrNetwork(dispatch, isError)) return;

    const { actualItems } = getTodoItemsFromPage(toDoItems, currentPage);
    const selectedItem = actualItems?.[verticalPosition];

    if (!selectedItem) return;

    if (selectedItem.toDoStatus === ToDoOfferItemToDoStatusEnum.Conflict) {
        dispatch(showRecordingConflictInfoOverlay(selectedItem));
    }
};

const getCurrentOfferPreview = async (
    item: ToDoOfferItem,
    programPreviewPromise: Promise<TOfferPreview>,
): Promise<TOfferPreviewCustomData | null> => {
    try {
        const preview = await programPreviewPromise;
        return preview.offerPreview;
    } catch {
        if (!item.offerId) {
            return null;
        }
        const service = new ServicesClientImpl();
        const res = await service.fetchOfferPreviewV2(item.offerId, ScreenType.All);
        return res.offerPreview;
    }
};

export const handleToDoListErrorOrNetwork = (dispatch: Function, isError: boolean) => {
    if (NetworkManager.isNetworkDisconnected()) {
        dispatch(
            showWhisper({
                message: NetworkManager.getNetworkDisconnectedMessage(),
                iconType: WhisperTypes.NO_ICON,
            }),
        );
        return true;
    }
    if (isError) {
        dispatch(onIncrementRetryFetchCounter());
        return true;
    }
    return false;
};

export const onIncrementRetryFetchCounter = () => (dispatch: Function, getState: Function) => {
    const { retryFetchCounter } = getState().todoList;
    dispatch(setRetryFetchCounterAction(retryFetchCounter + 1));
};

export default todoListReducer;
